import { useEffect, useState } from "react";
import { getMyWallet, getWalletTransactions, initiateTopup } from "../../api/client";
import BottomNav from "../../components/BottomNav";
import UserPageHeader from "../../components/UserPageHeader";
import LoadingIndicator from "../../components/LoadingIndicator";

export default function UserWallet() {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topupAmount, setTopupAmount] = useState("");
  const [topupGateway, setTopupGateway] = useState("paystack");
  const [topupLoading, setTopupLoading] = useState(false);
  const [topupError, setTopupError] = useState("");
  const [topupSuccess, setTopupSuccess] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [wRes, tRes] = await Promise.all([getMyWallet(), getWalletTransactions()]);
      setWallet(wRes.data);
      setTransactions(tRes.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleTopup = async (e) => {
    e.preventDefault();
    setTopupError("");
    setTopupSuccess("");
    const amount = Number(topupAmount);
    if (!amount || amount < 100) {
      setTopupError("Minimum top-up is ₦100");
      return;
    }
    setTopupLoading(true);
    try {
      const email = localStorage.getItem("userEmail") || "user@example.com";
      const { data } = await initiateTopup(amount, topupGateway, email);
      if (data.isMocked) {
        const { verifyTopup } = await import("../../api/client");
        await verifyTopup(data.reference, topupGateway, amount);
        setTopupSuccess(`₦${amount} added to your wallet successfully!`);
        setTopupAmount("");
        await load();
      } else if (data.checkoutUrl) {
        window.open(data.checkoutUrl, "_blank");
        setTopupSuccess("Payment page opened. After completing payment, come back and your balance will be updated.");
      }
    } catch (err) {
      setTopupError(err.response?.data?.error || "Top-up failed. Please try again.");
    } finally {
      setTopupLoading(false);
    }
  };

  return (
    <div className="user-app">
      <BottomNav />
      <div className="user-app-main">
        <UserPageHeader
          walletBalance={wallet?.balance ?? null}
          backTo="/order"
          backLabel="Send Package"
        />

        <div className="page">
        <div className="brand-header">
          <span className="brand-logo">💰</span>
          <span className="brand-name">My Wallet</span>
          <p className="brand-tagline">Top up and pay for deliveries instantly</p>
        </div>

        <div className="surge-toggle" style={{ background: "rgba(22, 199, 132, 0.1)", borderColor: "rgba(22, 199, 132, 0.3)", marginBottom: "16px" }}>
          <span style={{ color: "var(--success)", fontWeight: 600, fontSize: "13px" }}>
            💚 Pay from Wallet at checkout and get 5% off every delivery
          </span>
        </div>

        {loading ? (
          <LoadingIndicator label="Loading wallet..." />
        ) : (
          <>
            {/* BALANCE CARD */}
            <div className="delivery-card" style={{ textAlign: "center", marginBottom: "24px" }}>
              <p className="hint" style={{ marginBottom: "4px" }}>Available Balance</p>
              <div className="quote-box" style={{ margin: 0, padding: "12px 0", background: "transparent", border: "none" }}>
                <div className="price-total" style={{ fontSize: "42px", margin: 0 }}>
                  ₦{(wallet?.balance || 0).toLocaleString()}
                </div>
              </div>
            </div>

            {/* TOP-UP FORM */}
            <form onSubmit={handleTopup}>
              <h3>Top Up Wallet</h3>

              <div className="form-field">
                <label>Amount (₦)</label>
                <input
                  type="number"
                  min="100"
                  step="100"
                  placeholder="e.g. 5000"
                  value={topupAmount}
                  onChange={e => setTopupAmount(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Gateway</label>
                <select value={topupGateway} onChange={e => setTopupGateway(e.target.value)}>
                  <option value="paystack">Paystack</option>
                  <option value="flutterwave">Flutterwave</option>
                </select>
              </div>

              {topupError && <p className="error">{topupError}</p>}
              {topupSuccess && (
                <p style={{ color: "var(--text-accent)", fontSize: "14px", marginBottom: "8px" }}>
                  ✓ {topupSuccess}
                </p>
              )}

              <button type="submit" disabled={topupLoading}>
                {topupLoading ? "Processing..." : "Top Up Now"}
              </button>
            </form>

            {/* TRANSACTION HISTORY */}
            <h3 style={{ marginTop: "28px" }}>Transaction History</h3>
            {transactions.length === 0 ? (
              <div className="empty-state">
                <span className="empty-state-icon">📋</span>
                <p className="empty-state-text">No transactions yet.</p>
              </div>
            ) : (
              transactions.map((tx, i) => (
                <div key={i} className="history-row">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <strong style={{ color: tx.type === "topup" ? "var(--text-accent)" : "var(--danger-hover)" }}>
                        {tx.type === "topup" ? "▲ Top-up" : "▼ Payment"}
                      </strong>
                      <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                        {tx.gateway && `via ${tx.gateway} · `}
                        {new Date(tx.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <strong style={{ fontSize: "16px", color: tx.type === "topup" ? "var(--text-accent)" : "var(--danger-hover)" }}>
                      {tx.type === "topup" ? "+" : "-"}₦{tx.amount.toLocaleString()}
                    </strong>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  </div>
);
}
